import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import clarityBannerLogo from "../../../assets/clarity-banner-logo.svg";
import { reportError } from "../../../lib/bugReporting";
import {
  useIsAgentRoleUSA,
  useIsAgentRoleIndia,
} from "../../../lib/roleContext";
import { paths } from "../../../lib/router";
import styles from "./Banner.module.css";
import { useDialerPullState } from "../../../lib/dialerPullContext";
import { CallContext } from "../../../lib/api/models";

const strings = {
  /* TODO: Remove demo pages */
  clarityLogoAlt: "1FB Clarity Logo",
  componentDemo: "Component Demo",
  toolsDemo: "Tools Demo",
  accountSearch: "Account Search",
  accountDetails: "Mock Link to Account Details",
  secureMailQueue: "SecureMail Queue",
};

const FETCH_VERSION_INTERVAL_SECONDS = 30;

const isDev = import.meta.env.DEV;
const showDemoLinks = import.meta.env.DEV || import.meta.env.MODE === "bhwlabs";
const viteVersion = import.meta.env.VITE_VERSION_CODE;

const getLinkStyling = ({ isActive }: { isActive: boolean }) =>
  classNames({
    [styles.link]: true,
    [styles.activeLink]: isActive,
  });

type Props = {
  isSearch?: boolean;
  isDetails?: boolean;
};

export default function Banner({ isSearch = false, isDetails = false }: Props) {
  const { shouldDialerPull, callConnected, callContext } = useDialerPullState();

  const [shouldReloadOnNavigation, setShouldReloadOnNavigation] =
    useState(false);

  const usaRole = useIsAgentRoleUSA();
  const indiaRole = useIsAgentRoleIndia();
  const canViewSecureMail = usaRole || indiaRole;

  const location = useLocation();
  const { callContext: locationCallContext } = location.state
    ? (location.state as {
        callContext: CallContext;
      })
    : {};

  useEffect(() => {
    if (isDev) {
      return;
    }

    function fetchJSONVersion() {
      fetch("/version.json")
        .then((version) => {
          version.json().then((obj) => {
            const jsonVersion = obj.version;
            setShouldReloadOnNavigation(
              jsonVersion && jsonVersion !== viteVersion,
            );
          });
        })
        .catch((err) => reportError(err));
    }

    fetchJSONVersion();
    const interval = setInterval(() => {
      fetchJSONVersion();
    }, FETCH_VERSION_INTERVAL_SECONDS * 1000);
    return () => clearInterval(interval);
  }, []);

  const pageIcon = useMemo(
    () => document.querySelector("link[rel~='icon']"),
    [],
  );

  useEffect(() => {
    if (callConnected && (isDetails || isSearch)) {
      pageIcon?.setAttribute("href", "/faviconConnected.svg");
    } else if (shouldDialerPull && isSearch) {
      pageIcon?.setAttribute("href", "/faviconWaiting.svg");
    } else if (callContext?.callId === "0" && isDetails) {
      pageIcon?.setAttribute("href", "/faviconPreview.svg");
    } else {
      pageIcon?.setAttribute("href", "/favicon.svg");
    }
  }, [
    pageIcon,
    shouldDialerPull,
    callConnected,
    isSearch,
    isDetails,
    callContext,
  ]);

  return (
    <div
      className={classNames({
        [styles.container]: true,
        [styles.containerWaiting]: shouldDialerPull && isSearch,
        [styles.containerConnected]: callConnected && (isDetails || isSearch),
        [styles.containerPreview]: callContext?.callId === "0" && isDetails,
      })}
    >
      <img
        className={styles.logo}
        src={clarityBannerLogo}
        alt={strings.clarityLogoAlt}
      />
      <nav>
        <ul className={styles.navList}>
          <li>
            <NavLink
              className={getLinkStyling}
              to={paths.accountSearch}
              reloadDocument={shouldReloadOnNavigation}
              state={
                locationCallContext
                  ? { callContext: { ...locationCallContext, dwbuid: null } }
                  : null
              }
            >
              {strings.accountSearch}
            </NavLink>
          </li>
          {canViewSecureMail ? (
            <li>
              <NavLink
                className={getLinkStyling}
                to={paths.secureMailQueue}
                reloadDocument={shouldReloadOnNavigation}
              >
                {strings.secureMailQueue}
              </NavLink>
            </li>
          ) : null}
          {showDemoLinks ? (
            <>
              <li>
                <NavLink
                  className={getLinkStyling}
                  to={paths.componentsDemo}
                  reloadDocument={shouldReloadOnNavigation}
                >
                  {strings.componentDemo}
                </NavLink>
              </li>
              <li>
                <NavLink
                  className={getLinkStyling}
                  to={paths.tools}
                  reloadDocument={shouldReloadOnNavigation}
                >
                  {strings.toolsDemo}
                </NavLink>
              </li>

              <li>
                <NavLink
                  className={getLinkStyling}
                  to={paths.accountDetails("1234")}
                  reloadDocument={shouldReloadOnNavigation}
                  state={{ callContext }}
                >
                  {strings.accountDetails}
                </NavLink>
              </li>
            </>
          ) : null}
        </ul>
      </nav>
    </div>
  );
}
